/**
 * Works out which company's job portal this is from the URL, at runtime, so
 * one static build serves every tenant:
 *
 *   https://acme.jobs.redstonehr.com/job/123   (subdomain)
 *   https://jobs.redstonehr.com/acme/job/123   (first path segment)
 *
 * REACT_APP_COMPANY_SLUG is only a local-development fallback (localhost
 * with no slug in the path).
 */

/** The portal's own host; tenant subdomains sit under it. */
export const PORTAL_HOST = (
  process.env.REACT_APP_PORTAL_HOST || "jobs.redstonehr.com"
).toLowerCase();

// Same rule as the API's company resolver (Clerk organisation slugs).
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

// First path segments that are portal pages, not companies.
const RESERVED_SEGMENTS = new Set([
  "job",
  "jobs",
  "apply",
  "job-details",
  "static",
  "assets",
  "favicon.ico",
  "manifest.json",
  "robots.txt",
]);

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0", "[::1]"]);

export type Tenant =
  | {
      slug: string;
      mode: "subdomain" | "path" | "env";
      /** Router basename: "/acme" in path mode, "" otherwise. */
      basename: string;
    }
  | { slug: null; mode: "none"; basename: "" };

export function isValidSlug(value: string): boolean {
  return SLUG_PATTERN.test(value);
}

export function resolveTenant(
  hostname: string,
  pathname: string,
  envSlug: string | undefined = process.env.REACT_APP_COMPANY_SLUG,
): Tenant {
  const host = hostname.toLowerCase();

  if (host.endsWith(`.${PORTAL_HOST}`)) {
    const sub = host.slice(0, -(PORTAL_HOST.length + 1));
    if (sub && !sub.includes(".") && sub !== "www" && isValidSlug(sub)) {
      return { slug: sub, mode: "subdomain", basename: "" };
    }
  }

  const first = decodeURIComponent(pathname.split("/").filter(Boolean)[0] ?? "")
    .toLowerCase();
  if (first && !RESERVED_SEGMENTS.has(first) && isValidSlug(first)) {
    return { slug: first, mode: "path", basename: `/${first}` };
  }

  const fallback = envSlug?.trim().toLowerCase();
  if (LOCAL_HOSTS.has(host) && fallback && isValidSlug(fallback)) {
    return { slug: fallback, mode: "env", basename: "" };
  }

  return { slug: null, mode: "none", basename: "" };
}

/** The tenant for the page the browser is on. */
export function currentTenant(): Tenant {
  return resolveTenant(window.location.hostname, window.location.pathname);
}
